import { Chance, TestUtil, UIUtil } from '@suntex.core/animation';
import { $Color, type Color } from '@suntex.core/color';
import { LogLevel, loggedContext } from '@suntex.core/logging';

import {
  DIV_POOL1,
  DIV_POOL1_ANSW,
  DIV_POOL2,
  DIV_POOL2_ANSW,
  DIV_START_SECONDS,
  MUL_POOL1,
  MUL_POOL1_ANSW,
  MUL_POOL2,
  MUL_POOL2_ANSW,
  ROUND3_DIV_POOL,
  ROUND3_DIV_POOL_ANSW,
  ROUND3_MUL_POOL,
  ROUND3_MUL_POOL_ANSW,
  VIF_MUL_DIV_DATA,
  mulDivTableRef,
} from './data/vifMulDivData';
import type { BoardGeometry } from './engine/BoardGeometry';
import { MUL_DIV_GEOMETRY } from './engine/BoardGeometry';
import { addSubPatternTable, buildProblemSet } from './engine/ProblemSource';
import { BlockBoardEngine } from './engine/blockBoardEngine';
import type { VifGameProgArgs } from './types';

const MUL = 3,
  DIV = 4;

@loggedContext({ level: LogLevel.Trace, autoLog: true })
class VIFMulDivGame extends BlockBoardEngine {
  private readonly blnFirstTimeLayer = [1, 1, 1, 1];
  private randomPatterns = -1;
  private pool1Used = [
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  ];
  private pool1 = MUL_POOL1;
  private pool1Answ = MUL_POOL1_ANSW;
  private pool2Used = [
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    0, 0, 0,
  ];
  private pool2 = MUL_POOL2;
  private pool2Answ = MUL_POOL2_ANSW;

  constructor(args: VifGameProgArgs) {
    super(args, 5.0);
  }

  protected get correctColor(): Color {
    return $Color.from('#000000');
  }

  protected get hoverAlpha(): number {
    return 0.2;
  }

  protected get geometry(): BoardGeometry {
    return MUL_DIV_GEOMETRY;
  }

  protected computeStartSeconds(gameFlag: number): number[] {
    return gameFlag === DIV ? DIV_START_SECONDS : [0, 70, 60, 50];
  }

  protected onGameFlagResolved(gameFlag: number): void {
    switch (gameFlag) {
      case DIV:
        this.pool1 = DIV_POOL1;
        this.pool1Answ = DIV_POOL1_ANSW;
        this.pool2 = DIV_POOL2;
        this.pool2Answ = DIV_POOL2_ANSW;
        break;
      default:
        break;
    }
  }

  protected onBeforeHideMobilePiece(): void {
    this.pool1Used = [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    ];
    this.pool2Used = [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    ];
  }

  protected onSetupGame(): void {
    UIUtil.setFrame(this.gameStage.opBtn1, this.gameFlag);
  }

  private fillFromPoolsRound4(intLayer: number): void {
    if (this.blnFirstTimeLayer[intLayer - 1] == 1) {
      this.topEQs.length = 0;
      this.bottomAnswers.length = 0;
      for (let p = 0; p < 9; p++) {
        let a = Chance.randomInt(0, 35);
        while (this.pool1Used[a] == 1) {
          a = Chance.randomInt(0, 35);
        }
        this.pool1Used[a] = 1;
        this.topEQs.push(this.pool1[a]!);
        this.bottomAnswers.push(this.pool1Answ[a]!);
      }
      for (let p = 0; p < 7; p++) {
        let a = Chance.randomInt(0, 27);
        while (this.pool2Used[a] == 1) {
          a = Chance.randomInt(0, 27);
        }
        this.pool2Used[a] = 1;
        this.topEQs.push(this.pool2[a]!);
        this.bottomAnswers.push(this.pool2Answ[a]!);
      }
      this.blnFirstTimeLayer[intLayer - 1] = 0;
    }
  }

  /** Appends 9 random-distinct entries from the 36-entry times-table pool (MUL/DIV round 3). */
  private fillRound3Extras(
    pool: readonly string[],
    poolAnsw: readonly string[],
    beforeDraw?: () => void,
  ): void {
    const Used = [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    ];
    for (let p = 0; p < 9; p++) {
      beforeDraw?.();
      let a = Chance.randomInt(0, 35);
      while (Used[a] == 1) {
        a = Chance.randomInt(0, 35);
      }
      Used[a] = 1;
      this.topEQs.push(pool[a]!);
      this.bottomAnswers.push(poolAnsw[a]!);
    }
  }

  protected populateProblem(): void {
    const gameFrame = this.gameFrame;
    const intLayer = this.intLayer;

    if (this.gameFlag == MUL) {
      //TestUtil.log("round -> " + gameFrame.round.toString());
      if (gameFrame.round == 4) {
        this.fillFromPoolsRound4(intLayer);
        return;
      }
      const ref = mulDivTableRef('mul', gameFrame.round, intLayer);
      const { topEQs, bottomAnswers } = buildProblemSet({
        kind: 'table',
        ref,
      });
      this.topEQs = topEQs;
      this.bottomAnswers = bottomAnswers;

      if (gameFrame.round == 3) {
        this.fillRound3Extras(ROUND3_MUL_POOL, ROUND3_MUL_POOL_ANSW);
      }
    } else if (this.gameFlag == DIV) {
      TestUtil.log('round -> ' + gameFrame.round.toString());
      if (gameFrame.round == 4) {
        this.fillFromPoolsRound4(intLayer);
        return;
      }
      const ref = mulDivTableRef('div', gameFrame.round, intLayer);
      const { topEQs, bottomAnswers } = buildProblemSet({
        kind: 'table',
        ref,
      });
      this.topEQs = topEQs;
      this.bottomAnswers = bottomAnswers;

      if (gameFrame.round == 3) {
        this.fillRound3Extras(ROUND3_DIV_POOL, ROUND3_DIV_POOL_ANSW, () => {
          TestUtil.log('topEqs->' + this.topEQs.toString());
          TestUtil.log('bottomAnswers->' + this.bottomAnswers.toString());
        });
      }
    } else {
      if (intLayer == 1) {
        const { topEQs, bottomAnswers } = buildProblemSet({
          kind: 'table',
          ref: VIF_MUL_DIV_DATA.fallback.layer1,
        });
        this.topEQs = topEQs;
        this.bottomAnswers = bottomAnswers;
      } else {
        if (intLayer == 2 && this.randomPatterns == -1) {
          this.randomPatterns = Chance.randomInt(0, 5);
        }
        const ref = addSubPatternTable(
          VIF_MUL_DIV_DATA.fallback,
          intLayer,
          this.randomPatterns,
        );
        const { topEQs, bottomAnswers } = buildProblemSet({
          kind: 'table',
          ref,
        });
        this.topEQs = topEQs;
        this.bottomAnswers = bottomAnswers;
      }
    }
  }

  protected finalizeNextProblem(): void {
    this.board.enabled = true;
    UIUtil.setFrame(this.gameStage.gameMeter, this.intLayer + 3);
    this.gameFrame.scoreboard.updateGame(this.intLayer);
    UIUtil.setFrame(this.gameStage.roundMeter, this.gameFrame.round + 3);
  }

  protected onRunAnimationMeterUpdate(): void {
    UIUtil.setFrame(this.gameStage.gameMeter, this.intLayer + 1);
  }
}

export function VIFMulDiv_prog(args: VifGameProgArgs) {
  new VIFMulDivGame(args).run();
}
